import { oneLine } from 'common-tags';
import { describeFilter } from 'components/dynamic-content/copy-util';
import { CuisineFilterButton } from 'utils/cuisine-util';
import { CityPageCityFragment, CityPageDishFragment, CityPageFilterFragment } from 'utils/gqlReactTypings.generated.d';

export interface IProps {
  zipPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>;
  zipCode: string;
  cuisineButtons: CuisineFilterButton[];
  dishes?: CityPageDishFragment[];
  numDishes: number;
  activeFilter?: CityPageFilterFragment;
}

// H2 Content defined in https://docs.google.com/spreadsheets/d/15UYD_auKOpJcNcyoShHq9vR9KDAe0SbMifpY5LNQ31A/edit#gid=529174371
export const generateZipPageH2: (IProps) => string = ({ zipCode, activeFilter }): string => {
  const { filterDescription, hasFoodOrGoods } = describeFilter(activeFilter);
  return oneLine`Browse ${filterDescription ?? ''} dishes, cuisines, and local shefs in ${zipCode} for
   delicious homemade ${filterDescription ?? ''}${hasFoodOrGoods ? '' : ' food'}`;
};

// Copy block below H2 defined in https://docs.google.com/spreadsheets/d/15UYD_auKOpJcNcyoShHq9vR9KDAe0SbMifpY5LNQ31A/edit#gid=529174371
export const generateZipPageH2CopyBlock: (IProps) => string = ({
  zipPage,
  zipCode,
  cuisineButtons,
  dishes,
  numDishes,
  activeFilter,
}): string => {
  const firstCuisine = cuisineButtons[0]?.category.title; // TODO: Gil Note: These should actually be first by num sales
  const secondCuisine = cuisineButtons[1]?.category.title;
  const popularDish = dishes?.length && dishes[0].name;

  if (activeFilter) {
    const { filterDescription, hasFoodOrGoods } = describeFilter(activeFilter);
    return oneLine`We know it's not always easy to find ${filterDescription}${
      hasFoodOrGoods ? '' : ' food'
    } when you're out and about, which is why Shef
     makes it easy in ${zipCode}! With ${numDishes ?? 'a smorgasbord of'} ${filterDescription} dishes, we have you
      covered in ${zipPage.cityName}!`;
  }

  return oneLine`Looking for homemade food delivery in ${zipPage.cityName}? Find food options in ${zipCode} or nearby,
   delivered to your door! With ${numDishes ?? 'a smorgasbord of'} dishes available for delivery in your area, from
    ${cuisineButtons.length} cuisines and cooking styles${firstCuisine ? `, including ${firstCuisine}` : ''}
    ${firstCuisine && secondCuisine ? `, and ${secondCuisine}` : ''}, prepared by one of our expert Shefs in
     ${zipPage.cityName}, cooked with recipes passed down through generations - like our most popular ${popularDish},
      or dishes uniquely tailored to ${zipPage.cityName} tastes! Order your ${zipPage.cityName} food delivery today,
       and taste amazing homemade dishes from around the world!`;
};

export const generateZipPageMetaDesc: (IProps) => string = ({ zipPage, zipCode, activeFilter }): string => {
  const cityStateName = `${zipPage.cityName}, ${zipPage.stateNameShort}`;
  if (activeFilter) {
    const { filterDescription, hasFoodOrGoods } = describeFilter(activeFilter);
    return oneLine`Looking for ${filterDescription}${
      hasFoodOrGoods ? '' : ' food'
    } delivery in ${cityStateName}? Shef delivers homemade
     ${filterDescription} meals in ${zipCode} for an authentic taste in your neighborhood! Order today!`;
  }

  return oneLine`Looking for food delivery near ${cityStateName}? Shef delivers homemade food in and around
   ${zipCode} for an authentic taste in your neighborhood! Order today!`;
};
